// /plan package card -> checkout entry link. Shared by the /plan mobile card
// (layout/landing-v2/Plan.js) and the direct plan link
// /plan/signin?plan=<months> (helpers/planDirectLink.js, Trello SALES
// TRACKER 626) so both send the visitor to the same checkout URL.
import { encryptString } from "./encryption";
import { shouldForceXtLane, isSinglePurchasePackage } from "./xtLaneFlag";
import { buildCleengCheckoutQuery } from "./cleengCheckoutLink";
import link from "../static/text/link";

// Static-IDR landings whose checkout link must carry the package's own display
// values. Other landings here (Cleeng, flashdeals) resolve theirs elsewhere.
export const DISPLAY_PARAM_PAGE_SOURCES = ["plan", "/deals"];

const getIndexInitPackage = (planContent) => {
  if (!planContent || !planContent.plan || !planContent.plan.length) return 0;
  if (planContent.plan.length === 1) {
    return 0;
  }
  const enabledPackageTypes = planContent.enabled_package_types || {};
  if (
    (enabledPackageTypes.subscription
      && enabledPackageTypes.single_purchase)
    || (enabledPackageTypes.subscription
      && enabledPackageTypes.single_purchase === false)
  ) {
    return 0;
  }
  return 1;
};

// The package cards /plan shows.
export const getPlanPackageList = (planContent) => {
  const packageList = planContent && planContent.plan;
  if (!packageList || !packageList.length) return [];
  const selectedPlan = packageList[getIndexInitPackage(planContent)];
  if (
    !selectedPlan
    || !selectedPlan.packages
    || !selectedPlan.packages.length
  ) {
    const planWithPackages = packageList.find(
      (item) => item && item.packages && item.packages.length
    );
    return (planWithPackages && planWithPackages.packages) || [];
  }
  return selectedPlan.packages;
};

export const buildPlanCheckoutHref = (item, {
  page_price,
  codeId,
  promo_price,
  page_source,
  bogoEnabled,
  language,
} = {}) => {
  const encryptId = encryptString(item.id);
  let hrefString = page_price
    ? `${page_price}?id=${encryptId}`
    : `${link.mobile.login(encryptId)}?state=register`;
  if (codeId) {
    hrefString += `&referral_id=${codeId}`;
  } else if (item.promo) {
    hrefString += `&active_promo=${item.promo.code}`;
  }
  if (promo_price) {
    hrefString += "&promo_price=true";
  }
  const cleengPrice = item && item.cleeng_price && item.cleeng_price.source === "cleeng"
    ? item.cleeng_price
    : null;
  const cleengQuery = buildCleengCheckoutQuery({
    cleengPrice,
    item,
    pageSource: page_source,
    bogoEnabled,
    language,
  });
  if (cleengQuery) {
    hrefString += `&${cleengQuery}`;
  }
  // /plan and /deals ship static IDR packages, so buildCleengCheckoutQuery emits
  // nothing and checkout falls back to the by-id package content — which carries no
  // retail strikethrough (show_retail_price is false) and no pricing.sub_price_text.
  // Forward the landing package's own values. BE sends retail_price: null on some of
  // these packages, and price_text is then the package retail.
  if (!cleengPrice && DISPLAY_PARAM_PAGE_SOURCES.includes(page_source)) {
    const displayParams = new URLSearchParams();
    const totalPriceText = item.disc_price_text || item.price_text;
    const retailPriceText = item.retail_price || item.price_text;
    const subPriceText = item.pricing && item.pricing.sub_price_text;

    if (totalPriceText) {
      displayParams.set("display_total_price_text", totalPriceText);
    }
    if (retailPriceText && retailPriceText !== totalPriceText) {
      displayParams.set("display_retail_price_text", retailPriceText);
    }
    if (subPriceText) {
      displayParams.set("display_sub_price_text", subPriceText);
    }

    const displayQuery = displayParams.toString();
    if (displayQuery) {
      hrefString += `&${displayQuery}`;
    }
  }
  // Trello 546 Phase 2: force every Plan.js card-select checkout onto the
  // xt-aware lane on semi-prod, including the page_price override branch
  // above — plan-v3/plan-v4 forward an unvalidated ?page_price straight
  // from ctx.query with no server-side route guard (unlike /plan,
  // /program, /flashdeals), so this must not depend on which href branch
  // fired. item is already resolved here, so skip forcing for a
  // SINGLE_PURCHASE/one-time pick (owner decision: stays on the legacy
  // path this round).
  if (shouldForceXtLane() && !isSinglePurchasePackage(item)) {
    hrefString += "&xt=1";
  }
  return hrefString;
};
